import React, { useEffect, useState } from "react";
import { normalizeCityName } from "../lib/indianCities";

export function resolveCityInput(value, cities, allowCustom, cityAliases = {}) {
  const normalized = normalizeCityName(value);
  const match = cities.find((name) => normalizeCityName(name) === normalized);
  return match || cityAliases[normalized] || (allowCustom ? value.trim() : "");
}

export function getCitySuggestions(query, cities, limit = 12, popularCities = [], cityAliases = {}) {
  const normalized = normalizeCityName(query);
  if (!normalized) return [];
  const popularOrder = new Map(popularCities.map((name, index) => [normalizeCityName(name), index]));
  const matchingAliases = new Set(
    Object.entries(cityAliases)
      .filter(([alias]) => normalizeCityName(alias).includes(normalized))
      .map(([, canonical]) => normalizeCityName(canonical))
  );
  return cities
    .filter((name) => normalizeCityName(name).includes(normalized) || matchingAliases.has(normalizeCityName(name)))
    .sort((a, b) => {
      const aName = normalizeCityName(a);
      const bName = normalizeCityName(b);
      const aPopular = popularOrder.has(aName);
      const bPopular = popularOrder.has(bName);
      if (aPopular !== bPopular) return aPopular ? -1 : 1;
      if (aPopular && popularOrder.get(aName) !== popularOrder.get(bName)) {
        return popularOrder.get(aName) - popularOrder.get(bName);
      }
      const aStartsWith = aName.startsWith(normalized);
      const bStartsWith = bName.startsWith(normalized);
      if (aStartsWith !== bStartsWith) return aStartsWith ? -1 : 1;
      return a.localeCompare(b);
    })
    .slice(0, limit);
}

export default function CitySearchField({
  city,
  cities,
  popularCities = [],
  cityAliases = {},
  onCityChange,
  listId,
  allowCustom = false,
  className,
  ...inputProps
}) {
  const [query, setQuery] = useState(city || "");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setQuery(city || "");
  }, [city]);

  const allCities = [...popularCities, ...cities.filter((name) => !popularCities.includes(name))];
  const suggestions = getCitySuggestions(query, allCities, 12, popularCities, cityAliases);

  const selectCity = (value) => {
    setQuery(value);
    setIsOpen(false);
    setActiveIndex(-1);
    onCityChange(resolveCityInput(value, allCities, allowCustom, cityAliases));
  };

  const updateQuery = (value) => {
    setQuery(value);
    setIsOpen(true);
    setActiveIndex(-1);
    onCityChange(resolveCityInput(value, allCities, allowCustom, cityAliases));
  };

  return (
    <div className="city-search-field">
      <input
        {...inputProps}
        type="search"
        className={className}
        value={query}
        onChange={(event) => updateQuery(event.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && suggestions.length) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) => (index + 1) % suggestions.length);
          } else if (event.key === "ArrowUp" && suggestions.length) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
          } else if (event.key === "Enter" && activeIndex >= 0 && suggestions[activeIndex]) {
            event.preventDefault();
            selectCity(suggestions[activeIndex]);
          } else if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
          }
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen && suggestions.length > 0}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {isOpen && suggestions.length > 0 && (
        <ul className="city-search-suggestions" id={listId} role="listbox">
          {suggestions.some((name) => popularCities.includes(name)) && (
            <li className="city-search-group" role="presentation">Where artists are available</li>
          )}
          {suggestions.map((name, index) => (
            <React.Fragment key={name}>
              {index > 0 && popularCities.includes(suggestions[index - 1]) && !popularCities.includes(name) && (
                <li className="city-search-group" role="presentation">More cities</li>
              )}
              {index === 0 && !popularCities.includes(name) && (
                <li className="city-search-group" role="presentation">More cities</li>
              )}
              <li role="presentation">
                <button
                  type="button"
                  id={`${listId}-option-${index}`}
                  className={`city-search-suggestion${index === activeIndex ? " active" : ""}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCity(name)}
                >
                  {name}
                </button>
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}